import { DEFAULT_TIMEZONE } from '@gg/shared';
import i18n from './i18n';

/** Event dates are shown in the event's time zone, whatever the visitor's device says. */
const timeZone = DEFAULT_TIMEZONE;

const capitalize = (s: string) => s.charAt(0).toLocaleUpperCase() + s.slice(1);

/** "12–13 juin" / "June 12 – 13"; a single day when there is no end. */
export function formatDateRange(
  start: string | null,
  end: string | null,
  locale = i18n.language,
): string | null {
  if (!start) return null;
  const f = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone });
  return end ? f.formatRange(new Date(start), new Date(end)) : f.format(new Date(start));
}

/** "Samedi 12 juin" (format cards) or "sam. 12 juin" (recap on the email step). */
export function formatDay(iso: string, style: 'long' | 'short', locale = i18n.language): string {
  const s = new Intl.DateTimeFormat(locale, {
    weekday: style,
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(new Date(iso));
  return style === 'long' ? capitalize(s) : s;
}

/** "12 janvier" / "January 12". */
export function formatDate(iso: string, locale = i18n.language): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone }).format(
    new Date(iso),
  );
}

/** "08:00" / "8:00 AM". */
export function formatTime(iso: string, locale = i18n.language): string {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone }).format(
    new Date(iso),
  );
}

/**
 * Last day a tier is on sale, from its exclusive end ("jusqu'au 31 décembre" for a tier
 * ending at midnight on 1 January).
 */
export function formatLastDay(endsAt: string, locale = i18n.language): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone }).format(
    new Date(new Date(endsAt).getTime() - 1000),
  );
}

export function formatNumber(n: number, locale = i18n.language): string {
  return new Intl.NumberFormat(locale).format(n);
}

/** "12 juin 2027, 14:05" in the event's time zone (back-office timestamps). */
export function formatDateTime(iso: string, locale = i18n.language): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso));
}

/** Offset of the event time zone from UTC at `utcMs`, in ms (+1 h / +2 h for Paris). */
function zoneOffsetMs(utcMs: number): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(utcMs))
      .map((p) => [p.type, Number(p.value)]),
  );
  const asUtc = Date.UTC(
    parts.year!,
    parts.month! - 1,
    parts.day!,
    parts.hour!,
    parts.minute!,
    parts.second!,
  );
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

/**
 * `<input type="datetime-local">` value ("2027-05-12T23:59") read as event time (Paris) →
 * ISO instant. Empty or invalid input → null. Around DST changes, the hour that occurs twice
 * in autumn reads as its second (winter) occurrence and the skipped spring hour is shifted.
 */
export function eventLocalToIso(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  const naive = Date.UTC(y, mo - 1, d, h, mi);
  let utc = naive - zoneOffsetMs(naive);
  utc = naive - zoneOffsetMs(utc);
  return new Date(utc).toISOString();
}

/** ISO instant → `datetime-local` value in event time (Paris); null → "". */
export function isoToEventLocal(iso: string | null): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  return new Date(ms + zoneOffsetMs(ms)).toISOString().slice(0, 16);
}
